import Vue from "vue";
import store from "@/store";
import VueRouter from "vue-router";

// Only what the router itself needs at boot stays statically imported - the
// navigation guard below runs on every route change, so AuthService and the
// keycloak mixin have to be present before the first navigation.
import AuthService from "../services/AuthService.js";
import keycloakMixin from "@/mixins/keycloakAuthMixin.js";

// Every view is loaded on demand instead of being bundled into the initial
// chunk. Previously all ~98 views (plus everything they pull in - Firebase,
// CKEditor, the report generators) had to download, parse and execute before
// `new Vue()` in main.js could run, which is what pushed the first API call
// several seconds past page load.
//
// Views are grouped into feature chunks via webpackChunkName so a section
// loads in one request rather than one request per screen.

const HomeView = () => import(/* webpackChunkName: "view-home" */ "../views/HomeView.vue");

const LoginView = () => import(/* webpackChunkName: "view-auth" */ "../views/LoginView.vue");
const InvitedLogin = () => import(/* webpackChunkName: "view-auth" */ "../views/InvitedLogin.vue");
const ResetPasswordView = () => import(/* webpackChunkName: "view-auth" */ "../views/ResetPasswordView.vue");
const ForgetPassword = () => import(/* webpackChunkName: "view-auth" */ "../views/ForgetPassword.vue");
const NotFoundView = () => import(/* webpackChunkName: "view-auth" */ "../views/NotFoundView.vue");
const InterviewNotActive = () => import(/* webpackChunkName: "view-auth" */ "../views/InterviewNotActive.vue");

const UsersView = () => import(/* webpackChunkName: "view-users" */ "../views/UsersView.vue");
const TeachersView = () => import(/* webpackChunkName: "view-users" */ "../views/TeachersView.vue");
const JobSeekersView = () => import(/* webpackChunkName: "view-users" */ "../views/JobSeekersView.vue");
const CreateTeacher = () => import(/* webpackChunkName: "view-users" */ "../views/CreateTeacher.vue");
const createJobSeekerView = () => import(/* webpackChunkName: "view-users" */ "../views/createJobSeekerView.vue");
const UpdateTeacherView = () => import(/* webpackChunkName: "view-users" */ "../views/UpdateTeacherView.vue");
const UpdateJobSeekerView = () => import(/* webpackChunkName: "view-users" */ "../views/UpdateJobSeekerView.vue");
const UserProfileHistory = () => import(/* webpackChunkName: "view-users" */ "@/views/UserProfileHistory.vue");

const RoleView = () => import(/* webpackChunkName: "view-roles" */ "../views/RoleView.vue");
const CreateRoleView = () => import(/* webpackChunkName: "view-roles" */ "../views/CreateRoleView.vue");
const UpdateRole = () => import(/* webpackChunkName: "view-roles" */ "../views/UpdateRole");
const RoleUser = () => import(/* webpackChunkName: "view-roles" */ "@/views/RoleUser.vue");

const MastersView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/MastersView.vue");
const ClustersView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/ClustersView.vue");
const SubjectCategoryView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/SubjectCategoryView.vue");
const BoardsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/BoardsView.vue");
const DemoTopicsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/DemoTopicsView.vue");
const BrandsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/BrandsView.vue");
const LevelsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/LevelsView.vue");
const LoBankView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/LOBankView.vue");
const SchoolsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/SchoolsView.vue");
const SkillsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/SkillsView.vue");
const SubjectsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/SubjectsView.vue");
const GradesView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/GradesView.vue");
const SkillsWeightageThresholdsView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/SkillsWeightageThresholdsView.vue");
const AudienceRolesView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/AudienceRolesView.vue");
const LeagueView = () => import(/* webpackChunkName: "view-masters" */ "../views/masters/LeagueView.vue");
const ViewSchoolData = () => import(/* webpackChunkName: "view-masters" */ "../views/ViewSchoolData.vue");

const QuestionBankView = () => import(/* webpackChunkName: "view-question-bank" */ "../views/QuestionBankView.vue");
const ViewQuestion = () => import(/* webpackChunkName: "view-question-bank" */ "../views/ViewQuestion.vue");
const QuestionPreview = () => import(/* webpackChunkName: "view-question-bank" */ "@/views/QuestionPreview.vue");
const PsychometryView = () => import(/* webpackChunkName: "view-question-bank" */ "../views/PsychometryView.vue");

const AssessmentView = () => import(/* webpackChunkName: "view-assessment" */ "../views/AssessmentView.vue");
const StaticAssessmentView = () => import(/* webpackChunkName: "view-assessment" */ "../views/StaticAssessmentView.vue");
const ViewStaticAssessment = () => import(/* webpackChunkName: "view-assessment" */ "@/views/ViewStaticAssessment.vue");
const CreateStaticAssessment = () => import(/* webpackChunkName: "view-assessment" */ "../views/CreateStaticAssessment.vue");
const StaticAssessmentEdit = () => import(/* webpackChunkName: "view-assessment" */ "@/views/StaticAssessmentEdit.vue");
const EditStaticAssessment = () => import(/* webpackChunkName: "view-assessment" */ "@/views/EditStaticAssessment.vue");
const AssessmentListingView = () => import(/* webpackChunkName: "view-assessment" */ "@/views/AssessmentListingView.vue");
const AssessmentListingEditQuestion = () => import(/* webpackChunkName: "view-assessment" */ "@/views/AssessmentListingEditQuestion.vue");

const CampaignView = () => import(/* webpackChunkName: "view-campaign" */ "../views/CampaignView.vue");
const CampaignUserView = () => import(/* webpackChunkName: "view-campaign" */ "../views/CampaignUserView.vue");
const CampaignCardFullView = () => import(/* webpackChunkName: "view-campaign" */ "../views/CampaignCardFullView.vue");
const CreateCampaign = () => import(/* webpackChunkName: "view-campaign" */ "../views/CreateCampaign.vue");
const CampaignTeachersResult = () => import(/* webpackChunkName: "view-campaign" */ "../views/CampaignTeachersResult.vue");
const CampaignJobSeekersResult = () => import(/* webpackChunkName: "view-campaign" */ "../views/CampaignJobSeekersResult.vue");
const MestaDashboard = () => import(/* webpackChunkName: "view-campaign" */ "@/views/MestaDashboard.vue");
const MestaCampaignUser = () => import(/* webpackChunkName: "view-campaign" */ "@/views/MestaCampaignUser.vue");

const ScheduleInterviewRounds = () => import(/* webpackChunkName: "view-interview" */ "@/views/ScheduleInterviewRounds.vue");
const InterviewFeedBackFrom = () => import(/* webpackChunkName: "view-interview" */ "../views/InterviewFeedbackForm.vue");
const InterviewReport = () => import(/* webpackChunkName: "view-interview" */ "@/views/InterviewReport.vue");
const InterviewHistory = () => import(/* webpackChunkName: "view-interview" */ "@/views/InterviewHistory.vue");
const IntervieweeList = () => import(/* webpackChunkName: "view-interview" */ "@/views/IntervieweeList.vue");
const RequestedSlots = () => import(/* webpackChunkName: "view-interview" */ "@/views/RequestedSlots.vue");

const DemoReport = () => import(/* webpackChunkName: "view-reports" */ "../views/DemoReport.vue");
const UserReport = () => import(/* webpackChunkName: "view-reports" */ "../views/UserReport.vue");
const TestReport = () => import(/* webpackChunkName: "view-reports" */ "../views/TestReport.vue");
const ReportTemplateGenerator = () => import(/* webpackChunkName: "view-reports" */ "../views/ReportTemplateGenerator.vue");
const CandidateUserReport = () => import(/* webpackChunkName: "view-reports" */ "@/views/CandidateUserReport.vue");

const CandidateList = () => import(/* webpackChunkName: "view-candidates" */ "@/views/CandidateList.vue");

const TeachersAppraisal = () => import(/* webpackChunkName: "view-outcomes" */ "../views/TeachersAppraisal.vue");
const TeachersPIP = () => import(/* webpackChunkName: "view-outcomes" */ "../views/TeachersPIP.vue");
const TeachersOthers = () => import(/* webpackChunkName: "view-outcomes" */ "../views/TeachersOthers.vue");
const TeachersTBD = () => import(/* webpackChunkName: "view-outcomes" */ "../views/TeachersTBD.vue");
const JobSeekersAppraisal = () => import(/* webpackChunkName: "view-outcomes" */ "../views/JobSeekersAppraisal.vue");
const JobSeekersPIP = () => import(/* webpackChunkName: "view-outcomes" */ "../views/JobSeekersPIP.vue");
const JobSeekersTBD = () => import(/* webpackChunkName: "view-outcomes" */ "../views/JobSeekersTBD.vue");
const JobSeekersOthers = () => import(/* webpackChunkName: "view-outcomes" */ "@/views/JobSeekersOthers.vue");

const ClusterTeachersAppraisal = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterTeachersAppraisal.vue");
const ClusterTeachersPIP = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterTeachersPIP.vue");
const ClusterTeachersTBD = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterTeachersTBD.vue");
const ClusterCampaignTeachersResult = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterCampaignTeachersResult.vue");
const ClusterJobSeekersAppraisal = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterJobSeekersAppraisal.vue");
const ClusterJobSeekersPIP = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterJobSeekersPIP.vue");
const ClusterJobSeekersTBD = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterJobSeekersTBD.vue");
const ClusterCampaignJobSeekersResult = () => import(/* webpackChunkName: "view-cluster-dashboard" */ "../views/ClusterDashboardPages/ClusterCampaignJobSeekersResult.vue");

const SchoolTeachersAppraisal = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolTeachersAppraisal.vue");
const SchoolTeachersPIP = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolTeachersPIP.vue");
const SchoolTeachersTBD = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolTeachersTBD.vue");
const SchoolJobSeekersAppraisal = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolJobSeekersAppraisal.vue");
const SchoolJobSeekersPIP = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolJobSeekersPIP.vue");
const SchoolJobSeekersTBD = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolJobSeekersTBD.vue");
const SchoolCampaignTeachersResult = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolCampaignTeachersResult.vue");
const SchoolCampaignJobSeekersResult = () => import(/* webpackChunkName: "view-school-dashboard" */ "../views/SchoolDashboardPages/SchoolCampaignJobSeekersResult.vue");

const ManagementDashboard = () => import(/* webpackChunkName: "view-analytics" */ "@/views/ManagementDashboard.vue");
const ViewChartData = () => import(/* webpackChunkName: "view-analytics" */ "@/views/ViewChartData.vue");
const TrendlineDashboard = () => import(/* webpackChunkName: "view-analytics" */ "@/views/TrendlineDashboard.vue");
const ComparisonDashboard = () => import(/* webpackChunkName: "view-analytics" */ "@/views/ComparisonDashboard.vue");
const MixpanelDashboard = () => import(/* webpackChunkName: "view-analytics" */ "@/views/MixpanelDashboard.vue");
const VoltDashboard = () => import(/* webpackChunkName: "view-analytics" */ "@/views/VoltDashboard.vue");

const TestView = () => import(/* webpackChunkName: "view-misc" */ "../views/TestView.vue");
const Support = () => import(/* webpackChunkName: "view-misc" */ "@/views/Support.vue");

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: {
      requiresAuth: true,
      title:'Hubble STAR-Admin'
    },
  },
  {
    path: "/campaignUser",
    name: "CampaignUserView",
    component: CampaignUserView,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"read"
    },
  },
  {
    path: "/interviewFeedback",
    name: "InterviewFeedbackForm",
    component: InterviewFeedBackFrom,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schedule-interview-rounds",
    name: "ScheduleInterviewRounds",
    component: ScheduleInterviewRounds,
    meta: {
      requiresAuth: true,
      permission:"interview",
      action:"create"
    },
  },
  {
    path: "/campaignCardFullView/:audienceType",
    name: "CampaignCardFullView",
    component: CampaignCardFullView,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"read"
    },
  },

  {
    path: "/update-jobseeker",
    name: "UpdateJobSeekerView",
    component: UpdateJobSeekerView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"update"
    },
  },
  {
    path: "/users",
    name: "users",
    component: UsersView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"read"
    },
  },
  {
    path: "/user-history",
    name: "user-histoy",
    component: UserProfileHistory,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"read"
    },
  },
  {
    path: "/reset",
    name: "ForgetPassword",
    component: ForgetPassword,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/create-jobseeker",
    name: "createJobSeekerView",
    component: createJobSeekerView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"create"
    },
  },
  {
    path: "/create-teacher",
    name: "CreateTeacher",
    component: CreateTeacher,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"create"
    },
  },
  {
    path: "/update-role",
    name: "UpdateRole",
    component: UpdateRole,
    meta: {
      requiresAuth: true,
      permission:"rolesAndPrivilage",
      action:"update"
    },
  },
  {
    path: "/update-teacher",
    name: "UpdateTeacherView",
    component: UpdateTeacherView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"update"
    },
  },
  {
    path: "/manageRole",
    name: "ManageRoleView",
    component: CreateRoleView,
    meta: {
      requiresAuth: true,      
      permission:"rolesAndPrivilage",
      action:"create"
    },
  },
  {
    path: "/registerAdmin",
    name: "InvitedLogin",
    component: InvitedLogin,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/reset-password",
    name: "ResetPasswordView",
    component: ResetPasswordView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/roles",
    name: "RoleView",
    component: RoleView,
    meta: {
      requiresAuth: true,
      permission:"rolesAndPrivilage",
      action:"read"
    },
  },
  {
    path: "/campaign",
    name: "campaignView",
    component: CampaignView,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"read"
    },
  },
  {
    path: "/teachers",
    name: "teachers",
    component: TeachersView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"read"
    },
  },
  {
    path: "/jobseekers",
    name: "JobSeekersView",
    component: JobSeekersView,
    meta: {
      requiresAuth: true,
      permission:"userManagement",
      action:"read"
    },
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/question-bank",
    name: "question bank",
    component: QuestionBankView,
    meta: {
      requiresAuth: true,
      permission:"questionBank",
      action:"read"
    },
  },
  {
    path: "/question-bank/view-question",
    name: "ViewQuestion",
    component: ViewQuestion,
    meta: {
      requiresAuth: true,
      permission: "questionBank",
      action: "read",
    },
  },
  {
    path: "/assessment",
    name: "assessment",
    component: AssessmentView,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"read"
    },
  },
  {
    path: "/static-assessment",
    name: "static assessment",
    component: StaticAssessmentView,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"read"
    },
  },
  {
  path: "/create-static-assessment",  
    name: "CreateStaticAssessment",
    component: CreateStaticAssessment,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"create"
    },
  },
    {
  path: "/view-static-assessment",  
    name: "ViewStaticAssessment",
    component: ViewStaticAssessment,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"read"
    },
  },
  {
    path: "/static-assessment/edit",
    name: "static assessment edit",
    component: StaticAssessmentEdit,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"update"
    },
  },
  {
    path: "/edit-static-assessment/",
    name: "edit static assessment",
    component: EditStaticAssessment,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"update"
    },
  },
  {
    path: "/masters",
    name: "masters",
    component: MastersView,
    meta: {
      requiresAuth: true,
      permission:"masters",
      action:"read"
    },
  },
  {
    path: "/clusters",
    name: "clusters",
    component: ClustersView,
    meta: {
      requiresAuth: true,
      permission:"cluster",
      action:"read"
    },
  },
  {
    path: "/subject-category",
    name: "subjectCategory",
    component: SubjectCategoryView,
    meta: {
      requiresAuth: true,
      permission:"subjectCategory",
      action:"read"
    },
  },
  {
    path: "/boards",
    name: "boards",
    component: BoardsView,
    meta: {
      requiresAuth: true,
      permission:"board",
      action:"read"
    },
  },
  {
    path: "/demoTopics",
    name: "demoTopics",
    component: DemoTopicsView,
    meta: {
      requiresAuth: true,
      permission:"topics",
      action:"read"
    },
  }, 
  {
    path: "/brands",
    name: "brands",
    component: BrandsView,
    meta: {
      requiresAuth: true,
      permission:"Brand",
      action:"read"
    },
  },
  {
    path: "/grades",
    name: "grades",
    component: GradesView,
    meta: {
      requiresAuth: true,
      permission:"Grade",
      action:"read"
    },
  },
  {
    path: "/notFound",
    name: "NotFoundView",
    component: NotFoundView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/levels",
    name: "levels",
    component: LevelsView,
    meta: {
      requiresAuth: true,
      permission:"Level",
      action:"read"
    },
  },
  {
    path: "/lo-banks",
    name: "lobanks",
    component: LoBankView,
    meta: {
      requiresAuth: true,
      permission:"learningObjective",
      action:"read"
    },
  },
  {
    path: "/schools",
    name: "schools",
    component: SchoolsView,
    meta: {
      requiresAuth: true,
      permission:"school",
      action:"read"
    },
  },
  {
    path: "/skills",
    name: "skills",
    component: SkillsView,
    meta: {
      requiresAuth: true,
      permission:"skills",
      action:"read"
    },
  },

  {
    path: "/skills-weightage-thresholds",
    name: "skills-weightage-thresholds",
    component: SkillsWeightageThresholdsView,
    meta: {
      requiresAuth: true,
      permission:"skillWeightageThreshold",
      action:"read"
    },
  },

  {
    path: "/audience-roles",
    name: "audience-roles",
    component: AudienceRolesView,
    meta: {
      requiresAuth: true,
      permission:"hirsUniqeRole",
      action:"read"
    },
  },

  {
    path: "/league",
    name: "LeagueView",
    component: LeagueView,
    meta: {
      requiresAuth: true,
      permission:"league",
      action:"read"
    },
  },

  {
    path: "/subjects",
    name: "subjects",
    component: SubjectsView,
    meta: {
      requiresAuth: true,
      permission:"subjects",
      action:"read"
    },
  },
  {
    path: "/test",
    name: "test",
    component: TestView,
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/campaignteachersresult",  
    name: "campaignteachersresult",
    component: CampaignTeachersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/campaignjobseekersresult",  
    name: "campaignjobseekersresult",
    component: CampaignJobSeekersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/teachersappraisal/:id",  
    name: "teachersappraisal",
    component: TeachersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/teacherspip",  
    name: "teacherspip",
    component: TeachersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/teachersothers",  
    name: "teachersothers",
    component: TeachersOthers,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/teacherstbd",  
    name: "teacherstbd",
    component: TeachersTBD,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/jobseekersappraisal",  
    name: "jobseekersappraisal",
    component: JobSeekersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/jobseekerspip",  
    name: "jobseekerspip",
    component: JobSeekersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/jobseekerstbd",  
    name: "jobseekerstbd",
    component: JobSeekersTBD,
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: "/jobSeekersOthers",  
    name: "jobSeekersOthers",
    component: JobSeekersOthers,
    meta: {
      requiresAuth: true,
    },
  },

  {
    path: "/clusterteachersappraisal/:id",  
    name: "clusterteachersappraisal",
    component: ClusterTeachersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clusterteacherspip",  
    name: "clusterteacherspip",
    component: ClusterTeachersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clusterteacherstbd",  
    name: "clusterteacherstbd",
    component: ClusterTeachersTBD,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clustercampaignteachersresult",  
    name: "clustercampaignteachersresult",
    component: ClusterCampaignTeachersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clusterjobseekersappraisal",  
    name: "clusterjobseekersappraisal",
    component: ClusterJobSeekersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clusterjobseekerspip",  
    name: "clusterjobseekerspip",
    component: ClusterJobSeekersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clusterjobseekerstbd",  
    name: "clusterjobseekerstbd",
    component: ClusterJobSeekersTBD,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/clustercampaignjobseekersresult",  
    name: "clustercampaignjobseekersresult",
    component: ClusterCampaignJobSeekersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schoolteachersappraisal/:id",  
    name: "schoolteachersappraisal",
    component: SchoolTeachersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schoolteacherspip",  
    name: "schoolteacherspip",
    component: SchoolTeachersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schoolteacherstbd",  
    name: "schoolteacherstbd",
    component: SchoolTeachersTBD,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schooljobseekersappraisal",  
    name: "schooljobseekersappraisal",
    component: SchoolJobSeekersAppraisal,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schooljobseekerspip",  
    name: "schooljobseekerspip",
    component: SchoolJobSeekersPIP,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schooljobseekerstbd",  
    name: "schooljobseekerstbd",
    component: SchoolJobSeekersTBD,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schoolcampaignteachersresult",  
    name: "schoolcampaignteachersresult",
    component: SchoolCampaignTeachersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/schoolcampaignjobseekersresult",  
    name: "schoolcampaignjobseekersresult",
    component: SchoolCampaignJobSeekersResult,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/CreateCampaign",  
    name: "CreateCampaign",
    component: CreateCampaign,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"create"
    },
  },
  {
    path: "/view/:schoolName", // Seeing here View Each School data
    name: "ViewSchoolData",
    component: ViewSchoolData,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/psychometry", // Seeing here View Each School data
    name: "PsychometryView",
    component: PsychometryView,
    meta: {
      requiresAuth: true,
      permission:"questionBank",
      action:"read"
    },
  },
  {
    path: "/demoreport/:campaignId/:userId", 
    name: "DemoReport",
    component: DemoReport,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/userreport", 
    name: "UserReport",
    component: UserReport,
    meta: {
      requiresAuth: true,
      permission:"campaign"
    },
  },
  {
    path: "/testreport/:campaignId/:userId",
    name: "TestReport",
    component: TestReport,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/report-template-generator",
    name: "ReportTemplateGenerator",
    component: ReportTemplateGenerator,
    meta: {
      requiresAuth: false,
    },
  },
  {
      path: "/interviewReport/:campaignId/:userId", 
      name: "InterviewReport",
      component: InterviewReport,
      meta: {
        requiresAuth: true,
      },
  },
  {
      path: "/questionPreview",
      name: "QuestionPreview",
      component:QuestionPreview,
      meta:{
        requiresAuth: true,
        permission:"questionBank",
        action:"read"
      }
  },
  {
      path: "/support",
      name: "Support",
      component:Support,
      meta:{
        requiresAuth: true,
      }
  },
  {
    path: "/roleUser",
    name: "roleUserView",
    component: RoleUser,
    props: true,
    meta: {
      requiresAuth: true,
      permission:"rolesAndPrivilage",
      action:"read"
    }
  },
  {
    path: "/mestacampaignuser",
    name: "mestaCampaignUser",
    component: MestaCampaignUser,
    props: true,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"read"
    }
  },
  {
    path: "/assessment-listing-view",
    name: "assessmentListingView",
    component: AssessmentListingView,
    props: true,
    meta: {
      requiresAuth: true,
      permission:"aseessment",
      action:"read"
    }
  },
  {
    path: "/testreport/",
    name: "TestReport",
    component: TestReport,
    meta: {
      requiresAuth: true,
    }
  },
  {
    path: "/assessment-listing-edit-question",
    name: "assessmentListingEditQuestion",
    component: AssessmentListingEditQuestion,
    props: true,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"update"
    }
  },
  {
    path:"/mestaDashboard",
    name:"mestaDashboard",
    component: MestaDashboard,
    meta:{
      requiresAuth: true,
    }
  },
  {
    path:"/candidate-list",
    name:"candidateList",
    component: CandidateList,
    meta:{
      requiresAuth: true,
      permission:"candidatesListing",
      action:"read"
    }
  },
  {
    path: "/candidateuserreport", 
    name: "CandidateUserReport",
    component: CandidateUserReport,
    meta: {
      requiresAuth: true,
      permission:"candidatesListing",
      action:"read"
    },
  },
  {
    path: "/interview-history",
    name: "InterviewHistory",
    component: InterviewHistory,
    meta: {
      requiresAuth: true,
      permission:"campaign",
      action:"read"
    }
  },
  {
    path: "/vgos-internal",
    name: "vgosInternal",
    component: HomeView,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: "/interview-notactive",
    name: "InterviewNotActive",
    component: InterviewNotActive,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: "/management-dashboard",
    name: "ManagementDashboard",
    component: ManagementDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/update-static-assessment",  
    name: "CreateStaticAssessment",
    component: CreateStaticAssessment,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"update"
    },
  },
  {
    path: "/assessment-listing-question",
    name: "assessmentListingEditQuestion",
    component: AssessmentListingEditQuestion,
    props: true,
    meta: {
      requiresAuth: true,
      permission:"assessment",
      action:"read"
    }
  },
  {
    path: "/view-chart-data",
    name: "viewChartData",
    component: ViewChartData,
    props: true,
    meta: {
      requiresAuth: true,
    }
  },
  {
    path: "/trendline-dashboard",
    name: "TrendlineDashboard",
    component: TrendlineDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/comparison-dashboard",
    name: "ComparisonDashboard",
    component: ComparisonDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/mixpanel-dashboard",
    name: "MixpanelDashboard",
    component: MixpanelDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/volt-dashboard",
    name: "VoltDashboard",
    component: VoltDashboard,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/interviewee-list',
    name: 'InterveiweeList',
    component: IntervieweeList,
    meta:{
      requiresAuth: true
    }
  },
  {
    path: '/requested-slots',
    name: 'requested-slots',
    component: RequestedSlots,
    meta: {
      requiresAuth: true,
      permission: "slotApprover",
      action: "read"
    }
  }
];

const router = new VueRouter({
  routes,
});

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || "Hubble STAR-Admin";
  console.log('path',to)
    if (!store.state.firstFullPath) {
      if(to.fullPath != '/login'){
        store.commit('setFirstFullPath', to.fullPath);
      }
  }
  if (to.path === '/vgos-internal') {
    await keycloakMixin.methods.initializeAuth(to.fullPath);
    return;
  }
  if (to.matched.some((record) => record.meta.requiresAuth)) {
  if (!await AuthService.isAuthenticated()) {
    
    const hasRedirect = !!to.query.redirect;
    if (to.path === '/login' && hasRedirect) {
      next(); // already on login with redirect param, don't encode again
    } else {
      next({
        path: "/login",
        query: { redirect: encodeURIComponent(to.fullPath) }
      });
    }
  } else {
    console.log('to-matched',to.matched)
    const matchedRecord = to.matched.find(r => r.meta.permission);

    const permissionName = matchedRecord?.meta.permission;
    const action = matchedRecord?.meta.action || 'read'; // default to read
    if (!permissionName) {
      next();
      return;
    }

    const permission = store.state.role?.[permissionName];
    console.log('permission',permission)
    if (permission?.[action]) {
      next();
    } else {
      next("/notFound");
    }
  }
} else {
  next();
}
});

/**
 * Recovers from a stale lazy-chunk reference after a deploy.
 *
 * Views are code-split, so each route fetches its own hashed chunk on demand.
 * A deploy changes those hashes, which means anyone holding the previous
 * index.html - an open tab, or a cached document - asks for a filename that no
 * longer exists. The import() rejects, the navigation aborts, and to the user
 * the menu simply stops responding, with nothing in the UI to explain it.
 *
 * Reloading pulls the new index.html and, with it, the new chunk names. The
 * guard below only reloads once per navigation attempt: if the fresh document
 * still cannot fetch the chunk, the cause is not staleness (offline, a bad
 * deploy, a blocked CDN) and looping would just hide it.
 */
const CHUNK_LOAD_FAILURE = /Loading (CSS )?chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module/i;
const RELOAD_FLAG = "chunk-reload-attempted";

router.onError((error) => {
  if (!CHUNK_LOAD_FAILURE.test(error?.message || "")) return;

  let alreadyTried = false;
  try {
    alreadyTried = sessionStorage.getItem(RELOAD_FLAG) === "true";
    sessionStorage.setItem(RELOAD_FLAG, "true");
  } catch (storageError) {
    // Private browsing and blocked storage both throw here. Reloading once
    // without the guard is better than not recovering at all.
  }

  if (!alreadyTried) window.location.reload();
});

// A completed navigation means chunks are resolving again.
router.afterEach(() => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch (storageError) {
    // Nothing to clear if storage is unavailable.
  }
});

export default router;
